import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { v4 as uuid } from 'uuid';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import type { CreateMitTransactionRequest, MitMandateDTO, MitTransactionDTO } from '../../core/models';
import { MitService, mitApiError, mitRejectionLabel } from '../../services/mit.service';
import {
  MitChargeLinesFieldsComponent,
  buildMitChargeLinesForm,
  mitChargeLinesFromForm
} from './mit-charge-lines-fields.component';

type ChargeOutcome = 'approved' | 'declined' | 'rejected' | 'review';

/**
 * Cobro manual. La `Idempotency-Key` se genera al abrir y solo se reutiliza tras un error de red;
 * cualquier respuesta con cuerpo la descarta y el siguiente cobro usa otra.
 */
@Component({
  selector: 'app-mit-charge-dialog',
  templateUrl: './mit-charge-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, DialogModule, ButtonModule, FloatLabelModule, InputTextModule,
    MitChargeLinesFieldsComponent]
})
export class MitChargeDialogComponent {
  private fb = inject(FormBuilder);
  private mitService = inject(MitService);

  mandate = input<MitMandateDTO | null>(null);
  visible = input(false);
  closed = output<void>();
  charged = output<MitTransactionDTO>();

  idempotencyKey = signal<string | null>(null);
  sending = signal(false);
  networkFailed = signal(false);
  error = signal<string | null>(null);
  result = signal<MitTransactionDTO | null>(null);
  private pendingRequest: CreateMitTransactionRequest | null = null;
  private inProgressRetried = false;

  outcome = computed<ChargeOutcome | null>(() => {
    const status = this.result()?.status;
    if (!status) return null;
    if (status === 'APPROVED') return 'approved';
    if (status === 'DECLINED') return 'declined';
    if (status === 'REJECTED') return 'rejected';
    return 'review';
  });

  lines = buildMitChargeLinesForm(this.fb);
  form = this.fb.group({
    lines: this.lines,
    concept: ['', [Validators.required, Validators.maxLength(250)]],
    externalReference: ['', Validators.maxLength(100)]
  });

  constructor() {
    effect(() => {
      if (this.visible()) this.startNew();
    });
  }

  rejectionText(transaction: MitTransactionDTO): string {
    return mitRejectionLabel(transaction.rejectionCode) || transaction.providerMessage || 'El cobro fue rechazado';
  }

  startNew(): void {
    this.idempotencyKey.set(uuid());
    this.result.set(null);
    this.error.set(null);
    this.networkFailed.set(false);
    this.pendingRequest = null;
    this.inProgressRetried = false;
    this.lines.clear();
    this.form.reset({ concept: '', externalReference: '' });
    this.form.enable();
  }

  close(): void {
    if (!this.sending()) this.closed.emit();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.sending()) return;
    const raw = this.form.getRawValue();
    this.pendingRequest = {
      lines: mitChargeLinesFromForm(this.lines),
      concept: raw.concept!.trim(),
      externalReference: raw.externalReference?.trim() || undefined
    };
    this.send();
  }

  /** Reenvía el mismo cuerpo con la misma llave; la API devuelve el resultado original si ya se procesó. */
  retry(): void {
    if (this.pendingRequest) this.send();
  }

  private send(): void {
    const mandate = this.mandate();
    const key = this.idempotencyKey();
    if (!mandate || !key || !this.pendingRequest) return;
    this.sending.set(true);
    this.error.set(null);
    this.networkFailed.set(false);
    this.form.disable();
    this.mitService.createTransaction(mandate.id, this.pendingRequest, key).subscribe({
      next: (response) => {
        this.sending.set(false);
        this.idempotencyKey.set(null);
        const transaction = response.body?.body ?? null;
        this.result.set(transaction);
        if (transaction) this.charged.emit(transaction);
      },
      error: (err: HttpErrorResponse) => this.onError(err)
    });
  }

  private onError(err: HttpErrorResponse): void {
    const hasBody = !!err.error && typeof err.error === 'object';
    if (err.status === 0 || (err.status >= 500 && !hasBody)) {
      this.sending.set(false);
      this.networkFailed.set(true);
      this.error.set('No hubo respuesta del servidor. Reintenta: se usará la misma solicitud y no se cobrará dos veces.');
      return;
    }
    const { message, code } = mitApiError(err, 'No se pudo realizar el cobro');
    if (err.status === 409 && code === 'REQUEST_IN_PROGRESS' && !this.inProgressRetried) {
      this.inProgressRetried = true;
      const seconds = Number(err.headers?.get('Retry-After')) || 2;
      setTimeout(() => this.send(), seconds * 1000);
      return;
    }
    this.sending.set(false);
    this.idempotencyKey.set(uuid());
    this.pendingRequest = null;
    this.form.enable();
    this.error.set(message);
  }
}
